import { useState, useEffect } from "react";
import {
  Box,
  Button,
  InputAdornment,
  LinearProgress,
  TextareaAutosize,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { GridColDef, GridRowParams, GridValueGetterParams, MuiEvent } from '@mui/x-data-grid';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import MailIcon from '@mui/icons-material/Mail';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import axios from "axios";
import { useForm, Resolver, Controller, SubmitHandler } from "react-hook-form";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from '@mui/material/InputBase';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import SupportIcon from '@mui/icons-material/ContactSupport';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import CloseIcon from '@mui/icons-material/Close';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import MenuItem from '@mui/material/MenuItem';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Link from '@mui/material/Link';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { alpha, styled } from '@mui/material/styles';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { setApplicationValues, setPageTitle } from "../../slices/applicationSlice";
import { IApplication } from "../../interfaces/IApplication";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { IFeeding } from "../../interfaces/Growing/IFeeding";
import FeedingDialog from "../../components/Growing/FeedingDialog";
import CompleteFeedingDialog from "../../components/Growing/CompleteFeedingDialog";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AssignmentIcon from '@mui/icons-material/AssignmentInd';
import SearchChips from "../../components/SearchChips";
import { ISearchChips } from "../../interfaces/Core/ISearchChips";


const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[300],
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      '&:hover, &.Mui-hovered': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity,
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  },
}));

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

type Anchor = 'top' | 'left' | 'bottom' | 'right';

interface IFormInput {
  Name: string
  Type: string
  Crew: string
}

const resolver: Resolver<IFormInput> = async (values) => {
  return {
    values: values.Name ? values : {},
    errors: !values.Name
      ? {
        Name: {
          type: 'required',
          message: 'This is required.',
        },
      }
      : {},
  };
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function Feeding() {

  const dispatch = useAppDispatch();

  useEffect(() => {
    let appState: IApplication = {
      pageTitle: "Feeding",
      backgroundPath: "/assets/images/growing.jpg"
    }
    dispatch(setPageTitle(appState));
  }, []);

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });

  const [assignmentRows, setAssignmentRows] = useState<IFeeding[]>([]);
  const [completeRows, setCompleteRows] = useState<IFeeding[]>([]);
  const [feeding, selectedFeeding] = useState<IFeeding>();
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    axios
      .get<IFeeding[]>('https://localhost:44324/api/Feedings')
      .then(res => {
        console.log(res.data);
        setAssignmentRows(res.data.filter(x => !x.isComplete));
        setCompleteRows(res.data.filter(x => x.isComplete));
      })
  }, [open]);



  const { register, handleSubmit, control, formState: { errors } } = useForm<IFormInput>({ resolver });

  const onSubmit: SubmitHandler<IFormInput> = data => {
    axios.post('https://localhost:44324/api/PatchingLists', {
      name: data.Name, generation: data.Type, assignedTo: data.Crew
    }).then((data) => {
      if (data.status === 200) {
        setOpen(false);
      };
    });
  };

  const toggleDrawer =
    (anchor: Anchor, open: boolean) =>
      (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
          event &&
          event.type === 'keydown' &&
          ((event as React.KeyboardEvent).key === 'Tab' ||
            (event as React.KeyboardEvent).key === 'Shift')
        ) {
          return;
        }

        setState({ ...state, [anchor]: open });
      };

  const list = (anchor: Anchor) => (
    <Box
      sx={{ width: anchor === 'top' || anchor === 'bottom' ? 'auto' : 350, height: anchor === 'top' || anchor === 'bottom' ? '50%' : 500 }}
      role="presentation"
      onClick={toggleDrawer(anchor, false)}
      onKeyDown={toggleDrawer(anchor, false)}

    >
      <Grid container spacing={0}>
        <Grid item xs={9}>
          <Typography variant='h5' m={2}>Action Menu</Typography>
        </Grid>
        <Grid item xs={2}>
          <IconButton
            size="large"
            aria-label="show more"
            aria-haspopup="true"
            onClick={handleClickHelpOpen}
            color="inherit"
          >
            <SupportIcon sx={{ fontSize: '50px' }} />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container spacing={1} padding={2}>
        <Grid item xs={6}>
          <Button variant="contained" color="primary" fullWidth sx={{ height: '150px' }} onClick={handleClickOpen}>
            <Typography variant="h5" color="#FFF" textAlign="left" fontWeight={600}>ASSIGN FEEDING</Typography>
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button variant="contained" color="primary" fullWidth sx={{ height: '150px' }} onClick={handleClickOpen}>
            <Typography variant="h5" color="#FFF" textAlign="left" fontWeight={600}>COMPLETE FEEDING</Typography>
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button variant="contained" color="primary" fullWidth sx={{ height: '150px' }} onClick={handleClickOpen}>
            <Typography variant="h5" color="#FFF" textAlign="left" fontWeight={600}>WEATHER</Typography>
          </Button>
        </Grid>
      </Grid>
    </Box>
  );


  const [helpOpen, setHelpOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClickHelpOpen = () => {
    setHelpOpen(true);
  };

  const handleHelpClose = () => {
    setHelpOpen(false);
  };


  const handleClose = () => {
    setOpen(false);
  };



  const columnsassignments: GridColDef[] = [
    { field: 'notes', headerName: 'Notes', width: 350 },
    { field: 'assignedto', headerName: 'Assigned To', width: 200, valueGetter: (params) => { return params.row.crew.name } },
    { field: 'waterpercent', headerName: 'Water %', width: 120 },
    { field: 'fertilizer', headerName: 'Fertilizer', width: 200, },
    { field: 'percentFertilizer', headerName: 'Fertilizer %', width: 120, },
    { field: 'chemical', headerName: 'Chemical', width: 200, },
    { field: 'percentChemical', headerName: 'Chemical %', width: 120, },
    { field: 'acid', headerName: 'Acid', width: 200, },
    { field: 'percentAcid', headerName: 'Acid %', width: 120, },
    { field: 'notes', headerName: 'Notes', width: 120, },

  ];

  const columnscompleted: GridColDef[] = [
    { field: 'notes', headerName: 'Notes', width: 350 },
    { field: 'assignedto', headerName: 'Assigned To', width: 200, valueGetter: (params) => { return params.row.crew.name } },
    { field: 'waterpercent', headerName: 'Water %', width: 120 },
    { field: 'fertilizer', headerName: 'Fertilizer', width: 200, },
    { field: 'percentFertilizer', headerName: 'Fertilizer %', width: 120, },
    { field: 'chemical', headerName: 'Chemical', width: 200, },
    { field: 'percentChemical', headerName: 'Chemical %', width: 120, },
    { field: 'acid', headerName: 'Acid', width: 200, },
    { field: 'percentAcid', headerName: 'Acid %', width: 120, },
    { field: 'notes', headerName: 'Notes', width: 120, },

  ];

  const handleDoubleClick = (x: GridRowParams<any>, y: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>) => {
    console.log(x.row);
    selectedFeeding(x.row);
    setOpen(true);
  }

  interface IPatchingRow {
    name: string,
    status: string,
    patchweek: number,
    generation: string
  }

  // const rows = [
  //   { id: 1, name: 'Snow', status: 'Jon', location: 35, patching_week: 5, patching_year: 10, projected_completion: 'testing' },
  // ];

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#010101' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: 0,
    borderRadius: 0,
    boxShadow: "none",
  }));


  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#89B219',
      },
    },
  });

  const handleDelete = () => {
    console.info('You clicked the delete icon.');
  };

  const [searchInformation, setSearchInformation] = React.useState<ISearchChips>();

  const handleSearchChips = (searchInformation: ISearchChips) => {
    setSearchInformation(searchInformation);
  }



  return (
    <div>
      <Grid container spacing={0} bgcolor="#FFF" >
        <ThemeProvider theme={darkTheme}>
          <SearchChips handleSearch={handleSearchChips}></SearchChips>
        </ThemeProvider>
        <Grid item xs={12} >
          <Box sx={{ width: '100%' }}>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={value} onChange={handleChange} aria-label="basic tabs example" >
                <Tab label="Assignments" {...a11yProps(0)} icon={<AssignmentIcon />} iconPosition="start" sx={{ fontSize: '1.5em', fontWeight: '700', letterSpacing: '0' }} />
                <Tab label="Completed" {...a11yProps(1)} icon={<CheckBoxIcon />} iconPosition="start" sx={{ fontSize: '1.5em', fontWeight: '700', letterSpacing: '0' }} />
              </Tabs>
            </Box>

            <TabPanel value={value} index={0}>
              <Grid item xs={12}>
                <Item sx={{ padding: "0px" }}>
                  <div style={{ height: 'calc(100vh - 158px)', width: '100%' }}>
                    <StripedDataGrid
                      onRowDoubleClick={(x, y) => handleDoubleClick(x, y)}
                      rows={assignmentRows}
                      columns={columnsassignments}
                      pageSize={20}
                      rowsPerPageOptions={[20]}
                      sx={{ borderColor: 'rgba(100,100,100,0.2)' }}
                      getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                      }
                    />
                  </div>
                </Item>
              </Grid>
            </TabPanel>
            <TabPanel value={value} index={1}>
              <Grid item xs={12}>
                <Item sx={{ padding: "0px" }}>
                  <div style={{ height: 'calc(100vh - 158px)', width: '100%' }}>
                    <StripedDataGrid
                      rows={completeRows}
                      columns={columnscompleted}
                      pageSize={20}
                      rowsPerPageOptions={[20]}
                      sx={{ borderColor: 'rgba(100,100,100,0.2)' }}
                      getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                      }
                    />
                  </div>
                </Item>
              </Grid>
            </TabPanel>
          </Box>
        </Grid>

      </Grid>
      <ThemeProvider theme={darkTheme}>
        <div>
          <BootstrapDialog
            onClose={handleHelpClose}
            aria-labelledby="customized-dialog-title"
            open={helpOpen}
          >
            <BootstrapDialogTitle id="customized-dialog-title" onClose={handleHelpClose}>
              Need Support?
            </BootstrapDialogTitle>
            <DialogContent dividers>
              <Typography sx={{ mb: 3 }} variant="body2" gutterBottom>
                If you are having issues or have questions, please contact us below.
              </Typography>
              <Button size='large' fullWidth variant="contained" startIcon={<SmartphoneIcon />} sx={{ mb: 1, bgcolor: '#89B219' }}>
                <Link href="tel:8003141797" underline="none" color="#FFF"><Typography variant='h6' fontWeight={700}>800-314-1797</Typography></Link>
              </Button>
              <Button size='large' fullWidth variant="contained" startIcon={<EmailIcon />} sx={{ mb: 1, bgcolor: '#89B219' }}>
                <Link href="mailto:bobsmarket@idatp.com" underline="none">
                  <Typography variant='h6' fontWeight={700} color="#FFF">support@idatp.com</Typography></Link>
              </Button>
            </DialogContent>
          </BootstrapDialog>
        </div>
      </ThemeProvider>

      <CompleteFeedingDialog open={open} handleClose={handleClose} feeding={feeding}></CompleteFeedingDialog>

    </div>


  );
}

const patchingWeeks = [
  { label: 'Week One', type: 'week_one' },
  { label: 'Week Two', type: 'week_two' },
  { label: 'Week Three', type: 'week_three' },
  { label: 'Week Four', type: 'week_four' },
];

interface IPatchingListType {
  label: string;
  type: string;
}

const patchingListTypes = [
  { label: 'Generation 1', value: 'gen_one' },
  { label: 'Generation 2', value: 'gen_two' },
  { label: 'Manual', value: 'manual' },
  { label: 'Blow Out', value: 'blow_out' },
];

const crews = [
  { label: 'Crew A', value: '1' },
  { label: 'Crew B', value: '2' },
  { label: 'Crew C', value: '3' },
  { label: 'Crew D', value: '4' },
];

const marks = [
  {
    value: 0,
    label: 'WK1',
  },
  {
    value: 5,
    label: 'WK2',
  },
  {
    value: 10,
    label: 'WK3',
  },
  {
    value: 15,
    label: 'WK4',
  },
];
